import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../api';
import { toast } from 'sonner';
import { LogIn, RefreshCw, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import '../components/client/canalTokens.css';

/* Pantalla de entrada (login) con el estilo de marca del onboarding/landing:
   fondo mint, logos transparentes Pilar Domínguez + MEFIR, botón teal, Montserrat.
   La lógica de autenticación NO cambia.

   DOS PUERTAS (misma marca, mismo /auth/login, distinto texto según el dominio):
   · canal.metodopilardominguez.com → puerta CLIENTE  ("Bienvenida a tu Canal Privado")
   · crm.metodopilardominguez.com   → puerta EQUIPO   ("Acceso a tu Canal Privado")
   En localhost se puede forzar con ?puerta=canal | ?puerta=crm. */

const TEAL = '#2C6675';
const TEAL2 = '#3C8696';

// Detecta qué puerta mostrar. Por dominio en producción; por ?puerta=… para probar en local.
function resolverPuerta() {
  try {
    const forzada = new URLSearchParams(window.location.search).get('puerta');
    if (forzada === 'crm' || forzada === 'canal') return forzada;
    const host = (window.location.hostname || '').toLowerCase();
    if (host.startsWith('canal.')) return 'canal';
    if (host.startsWith('crm.')) return 'crm';
    // localhost / preview: sigue el modo de app forzado (?modo=cliente|staff).
    const modo = sessionStorage.getItem('modo_app_demo');
    if (modo === 'cliente') return 'canal';
    if (modo === 'staff') return 'crm';
  } catch (e) { /* entorno raro → equipo por defecto */ }
  return 'crm';
}

// Copy por puerta: la CLIENTE (canal) va por i18n (bilingüe ES/CA); la de EQUIPO
// (crm) es exclusiva de staff y queda en castellano (CRM interno en español).
const copyPuerta = (t, puerta) => (puerta === 'canal'
  ? { titulo: t('login.tituloCanal'), sub: t('login.subCanal') }
  : { titulo: 'Acceso CRM', sub: 'Introduce tus credenciales para acceder.' });

const S = {
  wrap: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    background: 'radial-gradient(130% 70% at 50% 0%,#E1F4EF 0%,#EAF6F4 55%)',
    fontFamily: "'Montserrat', system-ui, sans-serif", padding: '32px 18px',
  },
  logos: { display: 'flex', alignItems: 'center', gap: 18, marginBottom: 22 },
  card: {
    background: '#fff', borderRadius: 22, boxShadow: '0 18px 50px rgba(44,107,118,.16)',
    width: '100%', maxWidth: 440, padding: '30px 28px 28px', boxSizing: 'border-box',
  },
  title: { margin: '0 0 4px', fontSize: 24, color: '#16323a', fontWeight: 800, textAlign: 'center' },
  desc: { margin: '0 0 22px', color: '#33474d', fontSize: 14.5, textAlign: 'center' },
  label: { fontWeight: 700, fontSize: 13, color: '#16323a' },
  input: {
    width: '100%', height: 46, borderRadius: 12, border: '2px solid #9db3ab', padding: '0 14px',
    fontSize: 16, color: '#16323a', outline: 'none', boxSizing: 'border-box', margin: '6px 0 16px',
    fontFamily: "'Montserrat', system-ui, sans-serif",
  },
  btn: (dis) => ({
    height: 48, borderRadius: 12, border: 'none', width: '100%', cursor: dis ? 'default' : 'pointer',
    fontWeight: 700, fontSize: 15.5, fontFamily: "'Montserrat', system-ui, sans-serif",
    background: dis ? '#cfe0e1' : `linear-gradient(135deg,${TEAL2},${TEAL})`, color: '#fff',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  }),
  btnGhost: {
    height: 46, borderRadius: 12, width: '100%', cursor: 'pointer', marginTop: 10,
    fontWeight: 600, fontSize: 14.5, fontFamily: "'Montserrat', system-ui, sans-serif",
    background: 'rgba(60,134,150,.10)', color: TEAL, border: '1px solid rgba(60,134,150,.35)',
  },
  link: {
    width: '100%', marginTop: 12, background: 'none', border: 'none', color: TEAL, fontSize: 13.5,
    cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit',
  },
};

const Logos = () => (
  <div style={S.logos}>
    {/* Logos en BLANCO (silueta) sobre el fondo oscuro del canal */}
    <img src="/logo-pd.png" alt="Pilar Domínguez" style={{ height: 38, width: 'auto', filter: 'brightness(0) invert(1)' }} />
    <span style={{ width: 1, height: 32, background: 'rgba(255,255,255,.35)' }} />
    <img src="/logo-mefir-trans.png" alt="Instituto MEFIR" style={{ height: 34, width: 'auto', filter: 'brightness(0) invert(1)' }} />
  </div>
);

/* PUERTA DE CLIENTA en OSCURO (Ann 2026-07-16: «muy corporativa y del canal», la
   tarjeta blanca no): tarjeta panel del canal, textos menta, inputs oscuros con
   filo teal. SOLO estilo; la lógica de acceso no se toca. La puerta EQUIPO (crm)
   se queda como estaba. */
const SC = {
  card: {
    background: 'linear-gradient(180deg,#0c2422,#0a1f1e)', borderRadius: 22,
    border: '1px solid rgba(75,179,189,.22)', boxShadow: '0 24px 60px rgba(0,0,0,.55)',
    width: '100%', maxWidth: 440, padding: '30px 28px 28px', boxSizing: 'border-box',
  },
  title: { margin: '0 0 4px', fontSize: 24, color: '#ffffff', fontWeight: 800, textAlign: 'center' },
  desc: { margin: '0 0 22px', color: '#c3d6d3', fontSize: 14.5, textAlign: 'center' },
  label: { fontWeight: 700, fontSize: 13, color: '#c3d6d3' },
  input: {
    width: '100%', height: 46, borderRadius: 12, border: '2px solid rgba(75,179,189,.35)', padding: '0 14px',
    fontSize: 16, color: '#ffffff', outline: 'none', boxSizing: 'border-box', margin: '6px 0 16px',
    background: 'rgba(255,255,255,.06)', fontFamily: "'Montserrat', system-ui, sans-serif",
  },
  link: { ...{
    width: '100%', marginTop: 12, background: 'none', border: 'none', fontSize: 13.5,
    cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit',
  }, color: '#4bb3bd' },
};

const Login = ({ onLogin }) => {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const { verify2FA } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const puerta = resolverPuerta();
  const copy = copyPuerta(t, puerta);
  // Estilos efectivos: puerta CLIENTA = tarjeta oscura del canal; EQUIPO = como siempre.
  const E = puerta === 'canal' ? { ...S, ...SC } : S;

  // PRIMERA PÁGINA con imagen de fondo (solo puerta CLIENTE / canal). Se capa una
  // veladura mint translúcida encima de la foto para que la tarjeta y el texto sigan
  // legibles. Si la imagen no existe todavía, se ve el degradado mint de siempre.
  const FONDO_CANAL = '/fondo-canal.jpg';
  const wrapStyle = puerta === 'canal'
    ? {
        ...S.wrap,
        background: `linear-gradient(rgba(7,26,26,.9),rgba(7,26,26,.94)), url('${FONDO_CANAL}') center/cover no-repeat, #071a1a`,
      }
    : S.wrap;

  // BLINDAJE F2 · dispositivo nuevo: pedir código enviado al email registrado
  const [challenge2FA, setChallenge2FA] = useState(null); // {message, email_enviado, detalle}
  const [codigo2FA, setCodigo2FA] = useState('');
  const [loading2FA, setLoading2FA] = useState(false);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setResetEmail(emailParam);
      setShowForgotPassword(true);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    try {
      await onLogin(email, password);
      // PUERTA DE CLIENTA (Ann 2026-07-16): marca local «en este navegador entra
      // una clienta» — sin datos personales. Con ella, la próxima visita al canal
      // va directa a este formulario en vez de a la landing comercial.
      if (puerta === 'canal') { try { localStorage.setItem('canal_clienta', '1'); } catch (err) { /* noop */ } }
      toast.success(t('login.exito'));
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      // BLINDAJE F2 · dispositivo nuevo → pantalla de código 2FA (Mensaje 2)
      if (status === 403 && detail?.code === 'REQUIERE_2FA') {
        setChallenge2FA(detail);
        setCodigo2FA('');
        return;
      }
      // ── CONTRATO · LA PANTALLA DE ACCESO NO MIENTE ────────────────────────────
      // QUÉ HACE: si el servidor explica el motivo REAL (403 = ficha de baja o
      //   desactivada · 429 = demasiados intentos), se enseña SU mensaje tal cual.
      // NO SE PUEDE CAMBIAR: volver a tapar todos los errores con
      //   `login.datosIncorrectos`. Eso le mintió a una clienta real durante DOS
      //   DÍAS (22-24 sep 2026): cambió la contraseña una y otra vez porque la
      //   pantalla se lo pedía, cuando su contraseña era correcta y lo que pasaba
      //   es que su ficha estaba marcada de baja. Se enteró el centro porque
      //   ELLA escribió, no porque el sistema avisara.
      // POR QUÉ EXISTE: el backend YA devuelve el motivo bueno
      //   (`server.py` → POST /auth/login) y esta pantalla lo tiraba a la basura.
      // POR QUÉ ES SEGURO (no filtra si un email existe): el 403 solo se emite
      //   DESPUÉS de verificar la contraseña. Un 401 (usuario o contraseña mal)
      //   sigue saliendo genérico, que es donde estaría el riesgo de enumeración.
      // LO VIGILA: backend/tests/test_TESTAUTOPISTA_login_no_miente.py (en el CI).
      const motivoReal = (status === 403 || status === 429) && typeof detail === 'string'
        ? detail.trim()
        : '';
      // Mensaje claro y visible dentro del formulario (no un toast que el usuario no ve)
      setLoginError(motivoReal || t('login.datosIncorrectos'));
    } finally {
      setLoading(false);
    }
  };

  // BLINDAJE F2 · verificar código de dispositivo nuevo y entrar
  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!codigo2FA.trim()) { toast.error(t('dosfa.escribeCodigo')); return; }
    setLoading2FA(true);
    try {
      await verify2FA(email, password, codigo2FA.trim());
      if (puerta === 'canal') { try { localStorage.setItem('canal_clienta', '1'); } catch (err) { /* noop */ } }
      toast.success(t('dosfa.verificado'));
      setChallenge2FA(null);
    } catch (error) {
      // El detail del backend llega en ES: nunca mostrarlo crudo (fuga CA) — siempre clave traducida
      toast.error(t('dosfa.codigoIncorrecto'));
    } finally {
      setLoading2FA(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      // Aviso temporal (2026-07-14): el envío automático de correos está en
      // pausa. Registramos la solicitud igualmente, pero NO prometemos el email:
      // dirigimos a la clienta al centro. Quitar cuando el email vuelva a operar.
      // Si salta el límite (429) hay que DECÍRSELO al usuario para que no
      // insista (Ann 2026-08-01 · caso Roso). El resto de errores se siguen
      // tragando a propósito: el backend responde igual exista o no el email
      // (anti-enumeración), así que mostramos el "solicitud recibida" neutro.
      let limite = false;
      await axios.post(`${API}/auth/forgot-password`, { email: resetEmail })
        .catch((err) => { if (err?.response?.status === 429) limite = true; });
      if (limite) {
        toast.error(t('comun.demasiadasSolicitudes'));
        return;
      }
      toast.success(t('recuperar.solicitudRecibida'));
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error(t('recuperar.errorSolicitar')); // detail del backend en ES → nunca crudo (fuga CA)
    } finally {
      setResetLoading(false);
    }
  };

  // BLINDAJE F2 · vista de verificación de dispositivo nuevo (Mensaje 2 + código)
  if (challenge2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 p-4">
        <Card className="w-full max-w-md shadow-lg" data-testid="challenge-2fa-card">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <ShieldCheck className="h-14 w-14 text-teal-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">{t('dosfa.titulo')}</CardTitle>
            <CardDescription className="text-center">
              {t('dosfa.descripcion')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4 text-center">
              {challenge2FA.email_enviado ? t('dosfa.enviadoOk') : t('dosfa.enviadoKo')}
            </p>
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="codigo-2fa">{t('dosfa.codigoLabel')}</Label>
                <Input
                  id="codigo-2fa"
                  data-testid="codigo-2fa-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={codigo2FA}
                  onChange={(e) => setCodigo2FA(e.target.value.replace(/\D/g, ''))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Button
                  type="submit"
                  data-testid="verify-2fa-button"
                  className="w-full bg-teal-500 hover:bg-teal-600"
                  disabled={loading2FA}
                >
                  {loading2FA ? (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="mr-2 h-4 w-4" />
                  )}
                  {t('dosfa.verificarEntrar')}
                </Button>
                {/* Reclamo del titular · hoy reutiliza la recuperación de contraseña
                    (al entrar de nuevo, token_version expulsa al resto de sesiones).
                    La Fase 3 lo sustituirá por el reclamo dedicado con kick atómico. */}
                <Button
                  type="button"
                  data-testid="reclamo-titular-button"
                  variant="outline"
                  className="w-full"
                  onClick={() => { setChallenge2FA(null); setResetEmail(email); setShowForgotPassword(true); }}
                >
                  {t('dosfa.soyPropietario')}
                </Button>
                <Button
                  type="button"
                  data-testid="back-from-2fa-button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setChallenge2FA(null)}
                >
                  {t('comun.volver')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div style={wrapStyle}>
        <Logos />
        <div style={E.card} data-testid="forgot-password-card">
          <h1 style={E.title}>{t('recuperar.titulo')}</h1>
          {/* Aviso discreto (2026-07-14): envío de correos con incidencia hoy.
              Sin anunciar caída del sistema. Quitar cuando el email vuelva. */}
          <div style={{
            background: '#fff7ed', border: '1px solid #f4c58a', borderRadius: 12,
            padding: '14px 16px', margin: '0 0 18px', color: '#7a4a12',
            fontSize: 14.5, lineHeight: 1.55, textAlign: 'left', fontWeight: 600,
          }}>
            {t('recuperar.aviso1')}<strong>{t('recuperar.avisoStrong')}</strong>{t('recuperar.aviso2')}
          </div>
          <p style={E.desc}>{t('recuperar.descripcion')}</p>
          <form onSubmit={handleForgotPassword}>
            <label htmlFor="reset-email" style={E.label}>{t('comun.email')}</label>
            <input id="reset-email" data-testid="reset-email-input" type="email" placeholder={t('comun.emailPlaceholder')}
              value={resetEmail} onChange={(e) => setResetEmail(e.target.value.trim())} required style={E.input} />
            <button type="submit" data-testid="send-reset-link-button" disabled={resetLoading} style={S.btn(resetLoading)}>
              {resetLoading ? <RefreshCw className="animate-spin" style={{ width: 16, height: 16 }} /> : t('recuperar.enviarEnlace')}
            </button>
            <button type="button" data-testid="back-to-login-button" style={S.btnGhost} onClick={() => setShowForgotPassword(false)}>
              {t('comun.volverLogin')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={wrapStyle}>
      <style>{puerta === 'canal' ? 'input::placeholder{color:#7fa39e;opacity:1}' : 'input::placeholder{color:#6b7d78;opacity:1}'}</style>
      <Logos />
      <div style={E.card} data-testid="login-card">
        <h1 style={E.title}>{copy.titulo}</h1>
        <p style={E.desc}>{copy.sub}</p>
        {loginError && (
          <div role="alert" data-testid="login-error" style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            background: '#fdecea', border: '1px solid #f0b7b0', color: '#8a2b20',
            borderRadius: 12, padding: '14px 16px', margin: '0 0 18px',
            fontSize: 14.5, lineHeight: 1.5, fontWeight: 500,
          }}>
            <span aria-hidden="true" style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.4 }}>!</span>
            <span>{loginError}</span>
          </div>
        )}
        <form onSubmit={handleSubmit} autoComplete="on">
          <label htmlFor="email" style={E.label}>{t('comun.email')}</label>
          {/* Ann 19-jul: el EMAIL sí autorrellena (comodidad, no es el secreto); la
              contraseña de abajo sigue protegida con new-password. */}
          <input id="email" data-testid="email-input" type="email" placeholder={t('comun.emailPlaceholder')} autoComplete="username"
            value={email} onChange={(e) => { setEmail(e.target.value.trim()); if (loginError) setLoginError(''); }} required style={E.input} />

          <label htmlFor="password" style={E.label}>{t('comun.password')}</label>
          {/* Ann 18-jul: autoComplete="new-password" evita que el navegador guarde/
              autocomplete la contraseña (importante en ordenadores compartidos). */}
          <input id="password" data-testid="password-input" type="password" placeholder={t('login.passwordPlaceholder')} autoComplete="new-password"
            value={password} onChange={(e) => { setPassword(e.target.value); if (loginError) setLoginError(''); }} required style={E.input} />

          <button type="submit" data-testid="login-button" disabled={loading} style={S.btn(loading)}>
            {loading ? <RefreshCw className="animate-spin" style={{ width: 16, height: 16 }} /> : <LogIn style={{ width: 16, height: 16 }} />}
            {t('comun.iniciarSesion')}
          </button>
          <button type="button" data-testid="forgot-password-link" style={E.link} onClick={() => setShowForgotPassword(true)}>
            {t('login.olvidaste')}
          </button>
          {puerta === 'canal' && (
            <>
              <p style={{ textAlign: 'center', margin: '16px 0 0', paddingTop: 14, borderTop: '1px solid rgba(75,179,189,.25)', fontSize: 14.5, color: '#c3d6d3' }}>
                {t('login.sinCuenta')} <a href="/?ver=canal" style={{ color: '#4bb3bd', fontWeight: 700, textDecoration: 'none' }}>{t('login.registrate')}</a>
              </p>
              {/* Puerta de clienta: BOTÓN visible (Ann 2026-07-18: «más visible») para
                  volver a la página principal SIN borrar la marca de clienta. */}
              <a href="/?ver=canal" data-testid="ver-landing-link" style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                height: 46, borderRadius: 12, marginTop: 14, textDecoration: 'none',
                fontWeight: 700, fontSize: 14.5, fontFamily: "'Montserrat', system-ui, sans-serif",
                background: 'rgba(75,179,189,.12)', color: '#4bb3bd', border: '1.5px solid rgba(75,179,189,.5)',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M11 18l-6-6 6-6" /></svg>
                {t('login.verCanal')}
              </a>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
